// Global
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import {
  Placeholder,
  RouteData,
  withDatasourceCheck,
  GetStaticComponentProps,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/types/component-props';
import ModalWrapper, { AnimationStyle, ModalSize } from 'src/helpers/ModalWrapper/ModalWrapper';
import { useEffect, useState } from 'react';
import { useModalIdContext } from 'lib/context/GenericModalIDContext';
import { getEnum } from 'lib/utils/get-enum';
import Component from 'src/helpers/Component/Component';
import { FormsConstants } from 'lib/constants/forms-constants';
import { mapItemFieldResultsToObject } from 'lib/graphql/mappers/map-item-field-results-to-object';
import { mapSearchResults } from 'lib/graphql/mappers/map-search-results';
import { IntegratedGraphQlResult } from 'lib/graphql/types/integrated-graphql-result';
import { ItemFieldResult } from 'lib/graphql/types/item-field-result';
import { ItemSearchResults } from 'lib/graphql/types/item-search-results';
export type GenericModalProps =
  Feature.EnterpriseWeb.Enterprise.Components.Modal.GenericModal.GenericModal & {
    fields: {
      children: RouteData[];
    };
  } & ComponentProps;
const GenericModal = (props: GenericModalProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { selectedModalId, setSelectedModalId, setIsGenericModalOpen, prevFocusedElementRef } =
    useModalIdContext();
  const [customOverlayCSS, setcustomOverlayCSS] = useState('');

  const desktopAnimationStyle =
    getEnum<AnimationStyle>(props.fields?.desktopAnimationStyle) || null;
  const mobileAnimationStyle = getEnum<AnimationStyle>(props.fields?.mobileAnimationStyle) || null;

  const displayCloseBar = props.fields?.displayCloseBar?.value || false;

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setIsGenericModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedModalId('');
    prevFocusedElementRef?.current?.focus();
    setIsGenericModalOpen(false);
  };

  const modalSize = getEnum<ModalSize>(props.fields?.modalSize) ?? 'large';

  useEffect(() => {
    if (selectedModalId) {
      selectedModalId === props.fields?.modalId.value && handleOpenModal();
    }
    if (selectedModalId === '') {
      handleCloseModal();
    }
    // "props.fields?.modalId.value" is coming directly from layout service.
    // "handleCloseModal" is a function which is not going to be changed.
    // We can ignore both suggested deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModalId]);

  useEffect(() => {
    function handleOpenGenericModalEvent(e: CustomEvent) {
      if (!!e?.detail?.modalId) {
        setSelectedModalId(e.detail.modalId);
      }
      if (!!e.detail?.form) {
        setcustomOverlayCSS('!z-[1001]');
        document.dispatchEvent(
          new CustomEvent(FormsConstants.Enterprise.openFormEvent, { detail: e.detail })
        );
      }
    }
    document.addEventListener(
      FormsConstants.Enterprise.openModalEvent,
      handleOpenGenericModalEvent
    );

    return () => {
      document.removeEventListener(
        FormsConstants.Enterprise.openModalEvent,
        handleOpenGenericModalEvent
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Component dataComponent="modal/genericmodal" {...props}>
      <ModalWrapper
        size={modalSize}
        handleClose={handleCloseModal}
        modalLabel={props.fields?.modalId.value}
        isModalOpen={isModalOpen}
        desktopAnimationStyle={desktopAnimationStyle}
        mobileAnimationStyle={mobileAnimationStyle}
        displayCloseBar={displayCloseBar}
        customOverlayclass={customOverlayCSS}
      >
        <Placeholder
          isGenericModal={isModalOpen}
          name="components"
          rendering={props.rendering}
          render={(components) => {
            return components.map((component, index) => <div key={index}>{component}</div>);
          }}
        />
      </ModalWrapper>
      <script
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `function openModal(modalId, formDetails) { document.dispatchEvent(new CustomEvent('${FormsConstants.Enterprise.openModalEvent}', { detail: { modalId: modalId, form: formDetails }})); }`,
        }}
      />
    </Component>
  );
};
type IntegratedGraphQl = IntegratedGraphQlResult<{
  item: {
    fields: ItemFieldResult[];
    children: ItemSearchResults<{
      template: { name: string };
      fields: ItemFieldResult[];
    }>;
  };
}>;

export const getStaticProps: GetStaticComponentProps = async (rendering) => {
  if (rendering.fields === undefined || !('data' in rendering.fields)) {
    return {};
  }

  const fields = rendering.fields as unknown as IntegratedGraphQl;
  const result = {
    fields: {
      ...mapItemFieldResultsToObject(fields.data.item.fields),
      children: mapSearchResults(fields.data.item.children, (child) => ({
        fields: {
          ...mapItemFieldResultsToObject(child.fields),
        },
      })),
    },
  };
  return result;
};
export default withDatasourceCheck()<GenericModalProps>(GenericModal);
